/* @flow */
import Vue from "vue";
import axios from "axios";
import normalize from "jsonapi-normalizer";
import oPath from "object-path";
import query from "query-string";

import { REMOVE_SESSION } from "./session";
import { NEW_NOTIFICATION, REPLACE_NOTIFICATION } from "./notifications";
import type { JsonApiEntity } from "@/store";

export const REPLACE_APPLICATION_NETWORKS = "REPLACE_APPLICATION_NETWORKS";
export const ADD_APPLICATION_NETWORKS = "ADD_APPLICATION_NETWORKS";
export const MODIFY_APPLICATION_NETWORK = "MODIFY_APPLICATION_NETWORK";
export const REMOVE_APPLICATION_NETWORK = "REMOVE_APPLICATION_NETWORK";
export const START_APPLICATION_NETWORK_FETCHING =
  "START_APPLICATION_NETWORK_FETCHING";
export const DONE_APPLICATION_NETWORK_FETCHING =
  "DONE_APPLICATION_NETWORK_FETCHING";
export const SET_APPLICATION_NETWORKS_TOTAL = "SET_APPLICATION_NETWORKS_TOTAL";

export const FETCH_APPLICATION_NETWORKS = "FETCH_APPLICATION_NETWORKS";
export const FETCH_APPLICATION_NETWORK = "FETCH_APPLICATION_NETWORK";
export const CREATE_APPLICATION_NETWORK = "CREATE_APPLICATION_NETWORK";
export const UPDATE_APPLICATION_NETWORK = "UPDATE_APPLICATION_NETWORK";
export const DELETE_APPLICATION_NETWORK = "DELETE_APPLICATION_NETWORK";
export const RESET_STATE = "RESET_STATE";

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
  fetch_status: {
    all: "",
  },
});

export default {
  state: initialState(),
  getters: {
    applicationNetworkDefaults: () => ({}),
    applicationNetworks: ({ entities = {} }: Object) =>
      //$FlowFixMe
      Object.keys(entities).map((key) => entities[key]),
    applicationNetworksKeyedByEui: (state: Object, getters: Object) => {
      const applicationNetworks = {};
      getters.applicationNetworks.forEach((appNet) => {
        applicationNetworks[appNet.eui] = appNet;
      });
      return applicationNetworks;
    },
    applicationNetworkById: (state: Object) => (id: string) =>
      state.entities[id] || {},
    applicationNetworkByEui:
      (state: Object, getters: Object) => (eui: string) =>
        getters.applicationNetworks.find((appNet) => appNet.eui === eui) || {},
    applicationNetworkByOrgId:
      (state: Object, getters: Object) => (organization_id: string) => {
        const appNetsForRequestedOrg = [];
        getters.applicationNetworks.forEach((appNet) => {
          if (appNet.organization_id === organization_id) {
            appNetsForRequestedOrg.push(appNet);
          }
        });
        return appNetsForRequestedOrg;
      },
  },
  mutations: {
    [RESET_STATE](state: Object) {
      Object.assign(state, initialState());
      state.entities = initialState().entities;
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities;
    },
    [START_APPLICATION_NETWORK_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = "loading";
    },
    [DONE_APPLICATION_NETWORK_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = "loaded";
    },
    [REPLACE_APPLICATION_NETWORKS](state: Object, { entities = {} }: Object) {
      state.entities = entities;
    },
    [ADD_APPLICATION_NETWORKS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      };
      state.statistics.total++;
    },
    [MODIFY_APPLICATION_NETWORK](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      };
    },
    [REMOVE_APPLICATION_NETWORK](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id);
      state.statistics.total--;
    },
    [SET_APPLICATION_NETWORKS_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total;
    },
  },
  actions: {
    [RESET_STATE]: async function (state: any) {
      this.commit(RESET_STATE, state);
    },
    [FETCH_APPLICATION_NETWORKS]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      try {
        commit(START_APPLICATION_NETWORK_FETCHING, { key: "all" });
        let pathstring = "/application_networks?limit=200";

        if (!!payload && payload.orgId) {
          pathstring =
            "/organizations/" + payload.orgId + "/application_networks";
        }

        const { data } = await axios.get(pathstring);

        commit(SET_APPLICATION_NETWORKS_TOTAL, {
          total: data.meta.pagination.collection_count,
        });
        const entities = {};
        data.data.map((appNet) => {
          entities[appNet.id] = {
            ...appNet.attributes,
            ...appNet.meta,
          };
        });

        commit(REPLACE_APPLICATION_NETWORKS, {
          entities,
        });
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: "Failed to load application networks.",
          detail: err.message,
          color: "error",
        });
      } finally {
        commit(DONE_APPLICATION_NETWORK_FETCHING, { key: "all" });
      }
    },
    [FETCH_APPLICATION_NETWORK]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      try {
        const {
          data: { data },
        } = await axios.get(`/application_networks/${payload.id}`);
        commit(ADD_APPLICATION_NETWORKS, {
          entities: {
            [data.id]: {
              ...data.attributes,
              ...data.meta,
            },
          },
        });
        return { success: true };
      } catch (err) {
        const status = oPath.get(err, "response.status");
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: "Failed to load application network.",
            detail: err.message,
            color: "error",
          });
        }
        return { success: false, status };
      }
    },
    [CREATE_APPLICATION_NETWORK]: async function (
      { commit, dispatch }: Object,
      payload: JsonApiEntity
    ) {
      try {
        const { data } = await axios.post("/application_networks", {
          data: {
            type: "application_network",
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        });
        const { entities } = normalize(data);
        if (!payload.suppressNotifications) {
          commit(ADD_APPLICATION_NETWORKS, {
            entities: entities.application_networks,
          });
        }
        dispatch(NEW_NOTIFICATION, {
          text: "Saved new application network!",
          color: "success",
        });
        return data.data.attributes.eui;
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: "Failed to save application network",
          detail: err.message,
          color: "error",
        });
      }
      return false;
    },
    [UPDATE_APPLICATION_NETWORK]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      let notificationId;
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: "Saving...",
          undoable: true,
          color: "secondary",
        });
      }

      try {
        const { data } = await axios.patch(
          `/application_networks/${payload.id}`,
          {
            data: {
              type: "application_network",
              attributes: payload.attributes,
              relationships: payload.relationships,
            },
          }
        );
        const { entities } = normalize(data);
        commit(
          MODIFY_APPLICATION_NETWORK,
          entities.application_networks[payload.id]
        );
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: "Saved!",
            color: "success",
            undoable: true,
          });
        }
        return true;
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: "Failed to update application network ",
          detail: err.message,
          color: "error",
        });
      }
      return false;
    },
    [DELETE_APPLICATION_NETWORK]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      let notificationId;
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting application network...`,
          color: "secondary",
        });
      }

      try {
        await axios.delete(
          `/application_networks/${payload.id}?${query.stringify(
            payload.options
          )}`
        );
        commit(REMOVE_APPLICATION_NETWORK, payload);
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted application network!`,
            color: "success",
          });
        }
        return true;
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: "Failed to delete application network ",
          detail: err.message,
          color: "error",
        });
      }
      return false;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/application_networks.js